import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  TextField,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Grid from "@mui/material/Grid";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { getBillingDeploymentReport } from "../../services/Dashboard";
import { getColumnsData } from "./DeploymentsReportColumns";
import { useQuery } from "react-query";
import { isValidDate } from "../../services/Utils";
import SearchIcon from "@mui/icons-material/Search";
import getDataGridToolbarProps from "../common/CustomToolbar";

const DEPLOYMENT_REPORT_STATUS = [
  { label: "Installation Completed", value: "INSTALLATION_COMPLETED" },
  { label: "Download Completed", value: "DOWNLOAD_COMPLETED" },
];

const DeploymentsReport = (props) => {
  const CustomToolbar = getDataGridToolbarProps("Deployments Report", true);
  const columns = getColumnsData();
  const [status, setStatus] = useState(DEPLOYMENT_REPORT_STATUS[0].value);

  const [startPicker, setStartPicker] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1, 0, 0, 0, 0),
  );
  const [endPicker, setEndPicker] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1, 0, 0, 0, 0),
  );

  const [startPickerErrorText, setStartPickerErrorText] = useState(null);
  const [endPickerErrorText, setEndPickerErrorText] = useState(null);

  const [startMonth, setStartMonth] = useState(startPicker.getMonth() + 1);
  const [startYear, setStartYear] = useState(startPicker.getFullYear());
  const [endMonth, setEndMonth] = useState(endPicker.getMonth() + 1);
  const [endYear, setEndYear] = useState(endPicker.getFullYear());

  const [rows, setRows] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [fetchData, setFetchData] = useState(true);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const { isLoading: logsLoading, isFetching } = useQuery(
    [status, startMonth, startYear, endMonth, endYear],
    getBillingDeploymentReport,
    {
      enabled: fetchData,
      onSuccess: ({ data }) => {
        const tblData = data.map((row, index) => ({ index, ...row }));
        setRows(tblData);
        setPage(0);
      },
      onSettled: () => {
        setFetchData(false);
      },
      refetchOnWindowFocus: false,
    },
  );

  const handleStartMonthChange = (val = null) => {
    if (!isValidDate(new Date(val))) {
      setStartPickerErrorText("Invalid Month!");
      return;
    }
    setStartPickerErrorText(null);
    setStartPicker(val);
  };

  const handleEndMonthChange = (val = null) => {
    if (!isValidDate(new Date(val))) {
      setEndPickerErrorText("Invalid Month!");
      return;
    }
    if (new Date(val) < new Date(startPicker)) {
      setEndPickerErrorText("End month must be after start month!");
      return;
    }
    setEndPickerErrorText(null);
    setEndPicker(val);
  };

  const handleStatusChange = (event) => {
    const {
      target: { value: status },
    } = event;
    setStatus(status);
  };

  const hasErrors = !!startPickerErrorText || !!endPickerErrorText;

  const processSearch = () => {
    if (
      !isValidDate(new Date(startPicker)) ||
      !isValidDate(new Date(endPicker))
    )
      return;
    if (new Date(endPicker) < new Date(startPicker)) return;
    setStartMonth(startPicker.getMonth() + 1);
    setStartYear(startPicker.getFullYear());
    setEndMonth(endPicker.getMonth() + 1);
    setEndYear(endPicker.getFullYear());
    setFetchData(true);
  };

  const totalPages = Math.ceil((rows?.length || 0) / pageSize) || 1;

  const commonProps = { refresh, setRefresh };

  return (
    <div className="fragmentContainer">
      <Grid container spacing={2}>
        <Grid item xs={12} md={12} lg={12}>
          <Card sx={{ height: "96px" }}>
            <CardContent className="tile-cards">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <FormControl variant="standard" sx={{ m: 1, minWidth: 220 }}>
                    <InputLabel>Deployment Status</InputLabel>
                    <Select
                      value={status}
                      onChange={handleStatusChange}
                      label="Deployment Status"
                    >
                      {DEPLOYMENT_REPORT_STATUS.map((item) => (
                        <MenuItem key={item.value} value={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
                    <DatePicker
                      disableFuture
                      label="Start Month"
                      views={["month", "year"]}
                      value={startPicker}
                      timezone="UTC"
                      onChange={(val) => handleStartMonthChange(val)}
                      renderInput={(params) => (
                        <TextField
                          size="small"
                          {...params}
                          variant="standard"
                          error={!!startPickerErrorText}
                          onKeyDown={(e) => e.preventDefault()}
                          helperText={startPickerErrorText}
                        />
                      )}
                      maxDate={endPicker || new Date()}
                      minDate={new Date("01-01-2023")}
                      required
                    />
                  </FormControl>

                  <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
                    <DatePicker
                      disableFuture
                      label="End Month"
                      views={["month", "year"]}
                      value={endPicker}
                      timezone="UTC"
                      onChange={(val) => handleEndMonthChange(val)}
                      renderInput={(params) => (
                        <TextField
                          size="small"
                          {...params}
                          variant="standard"
                          error={!!endPickerErrorText}
                          onKeyDown={(e) => e.preventDefault()}
                          helperText={endPickerErrorText}
                        />
                      )}
                      maxDate={new Date()}
                      minDate={startPicker || new Date("01-01-2023")}
                      required
                    />
                  </FormControl>

                  <Button
                    disabled={hasErrors}
                    sx={{ ml: "8px" }}
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    onClick={() => processSearch()}
                  >
                    Search
                  </Button>
                </div>
              </LocalizationProvider>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={12} lg={12}>
          <Card>
            <CardContent sx={{ padding: 0, ":last-child": { padding: 0 } }}>
              <Box sx={{ height: "calc(100vh - 280px)", width: "100%" }}>
                <DataGrid
                  loading={logsLoading || isFetching}
                  getRowId={(row) => row?.index}
                  rows={rows || []}
                  columns={columns}
                  checkboxSelection
                  disableSelectionOnClick
                  experimentalFeatures={{ newEditingApi: true }}
                  page={page}
                  onPageChange={(newPage) => setPage(newPage)}
                  pageSize={pageSize}
                  onPageSizeChange={(newPageSize) => {
                    setPageSize(newPageSize);
                    setPage(0);
                  }}
                  components={{ Toolbar: CustomToolbar }}
                  componentsProps={{
                    toolbar: {
                      totalPages,
                      onPageChange: (newPage) => setPage(newPage),
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default DeploymentsReport;
